/**
 * Raise a real OS notification from the PAGE.
 *
 * This is the exact complement of the `push` handler in
 * `public/push-handlers.js`, and the two must stay complements:
 *
 *   worker fires  <=>  no window is visible   (the app is in the background)
 *   page fires    <=>  this window is visible (the app is in front of you)
 *
 * Neither can do the other's job. The worker cannot know *which* conversation
 * you are looking at, so it must not stay silent about the other twenty; the
 * page does know, and is the only place that can suppress a notification about
 * the very thing on screen.
 *
 * They share a `tag`, so if both ever fire for one event — a race as the tab is
 * hidden, or several tabs open at once — the OS collapses them into a single
 * notification instead of stacking duplicates.
 *
 * **Two ways to raise one, and the order is not a preference.** Android Chrome
 * THROWS on `new Notification()` from a page and demands
 * `registration.showNotification` — so on a phone, the constructor path is not
 * a fallback, it is the path that does not work. We ask the service worker
 * first wherever there is one; its `notificationclick` handler then routes the
 * click, which also means one click behaviour instead of two.
 */

const DEFAULT_ICON = '/icons/icon-192.png';

export function canShowSystemNotification() {
  return typeof window !== 'undefined'
    && 'Notification' in window
    && Notification.permission === 'granted';
}

/**
 * @param {object} opts
 * @param {string} opts.title
 * @param {string} opts.body
 * @param {string} [opts.tag]      groups/replaces notifications about one subject
 * @param {string} [opts.url]      where a click should land
 * @param {string} [opts.appType]  picks the service worker's click fallback
 * @param {string} [opts.icon]
 * @param {function} [opts.onClick] used only on the page-constructor path
 * @returns {Promise<boolean>} whether a notification was actually raised
 */
export async function showSystemNotification({
  title, body, tag, url, appType = 'default', icon, onClick,
}) {
  if (!canShowSystemNotification()) return false;
  // Only while this window is in front — otherwise the service worker owns it.
  if (document.visibilityState !== 'visible') return false;

  const options = {
    body: body || '',
    icon: icon || DEFAULT_ICON,
    badge: DEFAULT_ICON,
    tag: tag || undefined,
    renotify: Boolean(tag),
    data: { url, appType },
  };

  // 1. Through the service worker — required on Android, and it gives the
  //    click the same handler a background push would get.
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        await registration.showNotification(title || 'Biztimize', options);
        return true;
      }
    } catch (err) {
      console.warn('[Notification] Service worker could not show it:', err);
    }
  }

  // 2. Straight from the page. Desktop browsers with no worker registered —
  //    which includes a dev session before the dev worker has activated.
  try {
    const notification = new Notification(title || 'Biztimize', options);
    notification.onclick = () => {
      try {
        window.focus();
      } catch {
        /* a focus the browser refuses is not a reason to drop the navigation */
      }
      notification.close();
      if (onClick) onClick();
    };
    return true;
  } catch (err) {
    // The in-app toast has already been shown by the caller, so this is a
    // downgrade and never a failure.
    console.warn('[Notification] Could not raise a system notification:', err);
    return false;
  }
}

export default showSystemNotification;
